import Checkbox from "../Checkbox";
import { KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { LitMenu, LitMenuItem } from "../Menu/LitMenu";
import TextField from "../TextField";
import Divider from "../Divider";
import { compactMenuItemCssVars } from "../Menu/menuStyles";
import { useIsInsideModal } from "@/hooks/useModalContext";

export interface MultiSelectOption {
  disabled?: boolean;
  value: string;
  label: string;
  /** Secondary line in the menu, muted like `MenuDropdown` description rows. */
  description?: string;
}

export interface MultiSelectProps {
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  supportingText?: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onSelect(v: string, checked: boolean): void;
  className?: string;
  onClear(): void;
  /** Optional bulk clear that deselects every option. Keeps the menu open. */
  onSelectNone?(): void;
  /** Label for the bulk-select action that calls `onClear`. Defaults to "Show All". */
  selectAllLabel?: string;
  /** Label for `onSelectNone`. Defaults to "Select none". */
  selectNoneLabel?: string;
  defaultLabel?: string;
  /** Closed-field value when selections exist. Defaults to a comma-separated label list. */
  selectedText?: string;
  skipShowAll?: boolean;
  /** When false, selecting all keeps the menu open. Defaults to true unless `onSelectNone` is provided. */
  closeOnSelectAll?: boolean;
  /** Applies to the default Material menu; searchable mode uses a custom anchored popup. */
  positioning?: "absolute" | "popover" | "fixed";
  /** Applies to the default Material menu; searchable mode uses a custom anchored popup. */
  noVerticalFlip?: boolean;
  searchable?: boolean;
}

let measurementContext: CanvasRenderingContext2D | null = null;

type PopupPosition = {
  left: number;
  top: number;
  width: number;
};

function measureTextWidth(text: string) {
  if (typeof document === "undefined") return text.length * 8;

  if (!measurementContext) {
    measurementContext = document.createElement("canvas").getContext("2d");
  }
  if (!measurementContext) return text.length * 8;

  measurementContext.font = "16px Inter, sans-serif";
  return measurementContext.measureText(text).width;
}

function truncateTextToWidth(text: string, maxWidth: number) {
  if (!text || maxWidth <= 0 || measureTextWidth(text) <= maxWidth) return text;

  const ellipsis = "...";
  let low = 0;
  let high = text.length;

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measureTextWidth(`${text.slice(0, mid).trim()}${ellipsis}`) <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return `${text.slice(0, low).trim()}${ellipsis}`;
}

export default function MultiSelect({
  label,
  required,
  disabled = false,
  error,
  errorText,
  supportingText,
  options,
  onClear,
  onSelect,
  onSelectNone,
  selectAllLabel = "Show All",
  selectNoneLabel = "Select none",
  className,
  selectedValues = [],
  defaultLabel = "Show All",
  selectedText,
  skipShowAll = false,
  closeOnSelectAll,
  positioning: positioningProp,
  noVerticalFlip: noVerticalFlipProp,
  searchable = false
}: MultiSelectProps) {
  const insideModal = useIsInsideModal();
  const positioning = positioningProp ?? (insideModal ? "fixed" : "absolute");
  const noVerticalFlip = noVerticalFlipProp ?? !insideModal;
  const shouldCloseOnSelectAll = closeOnSelectAll ?? !onSelectNone;
  const [showOptions, setShowOptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputWidth, setInputWidth] = useState(0);
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);

  const selectedLabels = options.filter(({ value }) => selectedValues.includes(value)).map(({ label }) => label);
  const pickedValues = selectedText ?? selectedLabels.sort().join(", ");

  const selectedTextMaxWidth = Math.max(inputWidth - (searchable ? 112 : 64), 0);
  // Callers pass `selectedText` as an intentional closed summary — do not ellipsize it.
  const displayText =
    (selectedText !== undefined
      ? selectedText || defaultLabel
      : truncateTextToWidth(pickedValues, selectedTextMaxWidth)) || defaultLabel;
  const searchableDisplayText =
    selectedText !== undefined ? selectedText : truncateTextToWidth(pickedValues, selectedTextMaxWidth);
  const filteredOptions = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    if (!searchable || !normalizedSearchQuery) return options;

    return options.filter(({ label, value, description }) =>
      [label, value, description].some((text) => text?.toLowerCase().includes(normalizedSearchQuery))
    );
  }, [options, searchQuery, searchable]);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const getTextFieldInput = () => {
    const innerInput = textFieldRef.current?.shadowRoot?.querySelector("input") as HTMLInputElement | undefined;
    return innerInput ?? textFieldRef.current;
  };

  const updateInputMeasurements = () => {
    const textField = textFieldRef.current;
    if (!textField) return;

    const rect = textField.getBoundingClientRect();
    setInputWidth(rect.width);
    setPopupPosition({
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width
    });
  };

  useEffect(() => {
    const textField = textFieldRef.current;
    if (!textField) return;

    updateInputMeasurements();

    const resizeObserver = new ResizeObserver(updateInputMeasurements);
    resizeObserver.observe(textField);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const closeSearchableOptions = () => {
    setShowOptions(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    if (!searchable || !showOptions) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        closeSearchableOptions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchable, showOptions]);

  useEffect(() => {
    if (!searchable || !showOptions) return;

    updateInputMeasurements();
    window.addEventListener("resize", updateInputMeasurements);
    window.addEventListener("scroll", updateInputMeasurements, true);

    return () => {
      window.removeEventListener("resize", updateInputMeasurements);
      window.removeEventListener("scroll", updateInputMeasurements, true);
    };
  }, [searchable, showOptions]);

  useEffect(() => {
    if (!searchable || !showOptions) return;

    const handleEscapeCapture = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (!containerRef.current?.contains(event.target as Node)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closeSearchableOptions();
      getTextFieldInput()?.blur();
      if (event.target instanceof HTMLElement) {
        event.target.blur();
      }
    };

    window.addEventListener("keydown", handleEscapeCapture, true);
    return () => {
      window.removeEventListener("keydown", handleEscapeCapture, true);
    };
  }, [searchable, showOptions]);

  useEffect(() => {
    if (!showOptions || highlightedIndex < 0) return;

    optionRefs.current[highlightedIndex]?.focus();
  }, [highlightedIndex, showOptions]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(filteredOptions.length > 0 ? filteredOptions.length - 1 : -1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  const renderOptionContent = ({ value, label, description, disabled = false }: MultiSelectOption) => {
    const checked = selectedValues.includes(value);

    return (
      <label
        onClick={(e) => {
          e.preventDefault();
        }}
        className="flex w-full min-w-0 cursor-pointer items-center gap-0"
      >
        <div className="flex shrink-0 items-center">
          <Checkbox onChange={() => {}} name={value} disabled={disabled} checked={checked} />
        </div>
        <div
          className={classNames("ml-3 flex min-w-0 flex-1 flex-col", description ? "justify-start" : "justify-center")}
        >
          <span
            className={classNames("text-body-large leading-tight text-sys-brand-on-surface", {
              "opacity-[0.38]": disabled
            })}
          >
            {label}
          </span>
          {description ? (
            <div className="text-body-small text-sys-brand-on-surface-variant leading-tight">{description}</div>
          ) : null}
        </div>
      </label>
    );
  };

  const handleSelectOption = (value: string, checked: boolean) => {
    onSelect(value, !checked);
    if (searchable) {
      getTextFieldInput()?.focus();
    }
  };

  const handleSelectAll = () => {
    onClear();
    if (shouldCloseOnSelectAll) {
      if (searchable) {
        closeSearchableOptions();
      } else {
        setShowOptions(false);
      }
    } else if (searchable) {
      getTextFieldInput()?.focus();
    }
  };

  const handleSelectNone = () => {
    onSelectNone?.();
    if (searchable) {
      getTextFieldInput()?.focus();
    }
  };

  const bulkActions = !skipShowAll ? (
    <>
      {searchable ? (
        <button
          type="button"
          role="menuitem"
          className="flex h-[45px] w-full items-center px-3 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sys-brand-primary"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSelectAll}
        >
          <span className="text-body-large leading-tight">{selectAllLabel}</span>
        </button>
      ) : (
        <LitMenuItem keepOpen style={{ minWidth: inputWidth }} onClick={handleSelectAll}>
          <span className="text-body-large leading-tight">{selectAllLabel}</span>
        </LitMenuItem>
      )}
      {onSelectNone ? (
        searchable ? (
          <button
            type="button"
            role="menuitem"
            className="flex h-[45px] w-full items-center px-3 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sys-brand-primary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSelectNone}
          >
            <span className="text-body-large leading-tight">{selectNoneLabel}</span>
          </button>
        ) : (
          <LitMenuItem keepOpen style={{ minWidth: inputWidth }} onClick={handleSelectNone}>
            <span className="text-body-large leading-tight">{selectNoneLabel}</span>
          </LitMenuItem>
        )
      ) : null}
      <Divider />
    </>
  ) : null;

  const handleSearchableKeyDown = (event: ReactKeyboardEvent) => {
    if (!searchable) return;

    if (event.key === "Tab") {
      closeSearchableOptions();
      return;
    }

    if (event.key === "Escape" && showOptions) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      closeSearchableOptions();
      getTextFieldInput()?.blur();
      if (event.target instanceof HTMLElement) {
        event.target.blur();
      }
      return;
    }

    if (filteredOptions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setShowOptions(true);
      setHighlightedIndex((currentIndex) => (currentIndex + 1 >= filteredOptions.length ? 0 : currentIndex + 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setShowOptions(true);
      setHighlightedIndex((currentIndex) => (currentIndex <= 0 ? filteredOptions.length - 1 : currentIndex - 1));
      return;
    }

    const shouldToggleHighlightedOption =
      (event.key === "Enter" || event.key === " " || event.key === "Spacebar") && highlightedIndex >= 0;

    if (shouldToggleHighlightedOption) {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option && !option.disabled) {
        handleSelectOption(option.value, selectedValues.includes(option.value));
      }
    }
  };

  // Native dialog top-layer stacks above document.body portals, so keep the menu
  // in-tree when inside a modal; otherwise portal to escape overflow clips.
  const searchableMenu = (
    <div
      ref={popupRef}
      role="menu"
      className={classNames(
        "z-[1100] max-h-[260px] overflow-y-auto rounded bg-sys-brand-surface shadow-lg",
        insideModal ? "absolute left-0 right-0 top-full mt-1" : "fixed"
      )}
      style={
        insideModal
          ? undefined
          : {
              left: popupPosition?.left ?? 0,
              top: popupPosition?.top ?? 0,
              minWidth: popupPosition?.width ?? inputWidth
            }
      }
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {bulkActions}

      {filteredOptions.length === 0 && (
        <div role="status" className="flex h-[45px] items-center px-3">
          <span className="text-body-large leading-tight text-sys-brand-on-surface-variant opacity-60">
            No options found
          </span>
        </div>
      )}

      {filteredOptions.map((option, index) => {
        const checked = selectedValues.includes(option.value);
        return (
          <div
            role="menuitemcheckbox"
            aria-checked={checked}
            aria-disabled={option.disabled}
            key={option.value}
            ref={(element) => {
              optionRefs.current[index] = element;
            }}
            tabIndex={-1}
            className={classNames(
              "flex w-full items-center px-3 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sys-brand-primary",
              option.description ? "min-h-[60px] py-1" : "h-[45px]",
              option.disabled ? "cursor-not-allowed" : "cursor-pointer",
              highlightedIndex === index &&
                "bg-sys-brand-surface-container-high ring-2 ring-inset ring-sys-brand-primary"
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (option.disabled) return;
              handleSelectOption(option.value, checked);
            }}
          >
            {renderOptionContent(option)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      ref={containerRef}
      data-testid="multiselect"
      className={classNames("relative select-none min-w-0", className)}
      onKeyDown={handleSearchableKeyDown}
    >
      <div
        className="w-full"
        onClick={(e) => {
          e.stopPropagation();
          if (searchable) {
            if (disabled) return;
            setShowOptions(true);
            getTextFieldInput()?.focus();
            return;
          }
          setShowOptions((showOptions) => !showOptions);
        }}
      >
        <TextField
          {...{ label, required, disabled, error, errorText, supportingText }}
          className="w-full min-w-0"
          ref={textFieldRef}
          value={searchable ? (showOptions ? searchQuery : searchableDisplayText) : displayText}
          placeholder={searchable ? (!showOptions && !pickedValues ? defaultLabel : "") : undefined}
          readOnly={!searchable}
          onChangeText={(value) => {
            if (!searchable) return;
            setSearchQuery(value);
            setShowOptions(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            if (searchable && !disabled) setShowOptions(true);
          }}
          leadingIcon={searchable ? "search" : undefined}
          trailingIcon="arrow_drop_down"
        />
      </div>

      {searchable ? (
        showOptions && (insideModal ? searchableMenu : createPortal(searchableMenu, document.body))
      ) : (
        <LitMenu
          anchorCorner="end-start"
          noHorizontalFlip
          noVerticalFlip={noVerticalFlip}
          menuCorner="start-start"
          anchorElement={textFieldRef.current}
          open={showOptions}
          positioning={positioning}
          onClosed={() => {
            setShowOptions(false);
          }}
          style={
            {
              ...compactMenuItemCssVars,
              "--md-menu-item-one-line-container-height": "45px",
              "--md-menu-item-two-line-container-height": "60px",
              "--md-menu-item-top-space": "4px",
              "--md-menu-item-bottom-space": "4px"
            } as React.CSSProperties
          }
        >
          {bulkActions}

          {options.map((option) => {
            const checked = selectedValues.includes(option.value);
            return (
              <LitMenuItem
                onClick={() => {
                  handleSelectOption(option.value, checked);
                }}
                disabled={option.disabled}
                keepOpen={true}
                key={option.value}
                selected={checked}
                style={{ minWidth: inputWidth }}
              >
                {renderOptionContent(option)}
              </LitMenuItem>
            );
          })}
        </LitMenu>
      )}
    </div>
  );
}
